import React, { useEffect, useState } from 'react'
import { Crown, Coins, Search, RefreshCw, Loader2 } from 'lucide-react'
import { membershipService, type AdminUserRow, type SiteKey, type SitePro } from '../../services/membership/membershipService'

const SITES: { key: SiteKey; label: string; color: string }[] = [
  { key: 'ebook', label: 'Ebook + PMaker', color: 'text-blue-700' },
  { key: 'eng', label: 'English', color: 'text-emerald-700' },
  { key: 'lab', label: 'Lab', color: 'text-cyan-700' },
]

// Trang quản trị: PRO Ebook+PMaker dùng chung; ENG/Lab tách riêng; điểm chung cho Ebook+PMaker.
const MembershipManager: React.FC = () => {
  const [rows, setRows] = useState<AdminUserRow[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [site, setSite] = useState('')
  const [role, setRole] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try { setRows(await membershipService.adminUsers(search, site, role, 300)) } catch (e: any) { alert(e.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  useEffect(() => { load() }, [site, role])

  const setPro = async (uid: string, s: SiteKey, tier: 'off' | 'trial' | 'pro') => {
    setBusy(`${uid}:${s}`)
    try { await membershipService.setSitePro(uid, s, tier); await load() } catch (e: any) { alert(e.message) } finally { setBusy(null) }
  }
  const adjust = async (uid: string) => {
    const v = prompt('Cộng/trừ điểm (số dương cộng, âm trừ):', '50')
    if (!v || isNaN(Number(v)) || Number(v) === 0) return
    setBusy(`${uid}:pts`)
    try { await membershipService.adjustPoints(uid, Number(v)); await load() } catch (e: any) { alert(e.message) } finally { setBusy(null) }
  }

  const curVal = (sp?: SitePro): 'off' | 'trial' | 'pro' => (!sp?.enabled ? 'off' : (sp.tier === 'trial' ? 'trial' : 'pro'))
  const badge = (sp?: SitePro) => {
    if (!sp?.enabled) return null
    if (sp.days_left < 0) return <span className="text-[9px] font-bold text-emerald-600">∞</span>
    if (!sp.active) return <span className="text-[9px] font-bold text-red-500">hết hạn</span>
    return <span className={`text-[9px] font-bold ${sp.days_left <= 3 ? 'text-red-500' : 'text-gray-500'}`}>{sp.days_left}d</span>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2"><Crown className="text-amber-500" /> Gói PRO & Điểm</h1>
          <p className="text-sm text-gray-500">Cấp PRO cho tài khoản: <b>Ebook + PMaker dùng chung một gói</b> — dùng thử 3 ngày hoặc <b>PRO 365 ngày giá 1.499k/năm</b>, kèm <b>100 điểm/tháng</b> reset đầu tháng. ENG và Lab là gói riêng; Lab dùng thử 30 ngày.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={site} onChange={e => setSite(e.target.value)} className="py-2 px-3 border border-gray-200 rounded-xl text-sm bg-white outline-none">
            <option value="">Tất cả site đăng ký</option>
            <option value="ebook">ebook</option><option value="pmaker">pmaker</option>
            <option value="eng">eng</option><option value="lab">lab</option>
            <option value="tronde">tronde</option><option value="vatli365">vatli365</option>
          </select>
          <select value={role} onChange={e => setRole(e.target.value)} className="py-2 px-3 border border-gray-200 rounded-xl text-sm bg-white outline-none">
            <option value="">Mọi vai trò</option><option value="teacher">Giáo viên</option><option value="student">Học sinh</option><option value="admin">Admin</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="Tìm theo tên/email…" className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm w-52 outline-none focus:border-blue-400" />
          </div>
          <button onClick={load} className="p-2 text-gray-500 hover:text-blue-600"><RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 font-black">
            <tr>
              <th className="px-4 py-3 text-left">Tài khoản</th>
              {SITES.map(s => <th key={s.key} className={`px-3 py-3 text-center ${s.color}`}>{s.label}</th>)}
              <th className="px-4 py-3 text-center">Điểm</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && rows.length === 0 ? (
              <tr><td colSpan={SITES.length + 2} className="py-12 text-center text-blue-500"><Loader2 className="animate-spin mx-auto" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={SITES.length + 2} className="py-12 text-center text-gray-400">Không có tài khoản.</td></tr>
            ) : rows.map(u => (
              <tr key={u.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2.5">
                  <div className="font-bold text-gray-900 leading-tight">{u.name}</div>
                  <div className="text-[11px] text-gray-400">{u.email}</div>
                  <div className="text-[10px] text-gray-400 uppercase">{u.role}{u.source_domain ? ` · ${u.source_domain}` : ''}</div>
                </td>
                {SITES.map(s => {
                  const sp = u.sites?.[s.key]
                  const val = curVal(sp)
                  return (
                    <td key={s.key} className="px-2 py-2.5 text-center">
                      <select
                        value={val}
                        disabled={busy === `${u.id}:${s.key}`}
                        onChange={e => setPro(u.id, s.key, e.target.value as any)}
                        className={`text-xs rounded-lg border px-1.5 py-1 outline-none ${val === 'off' ? 'border-gray-200 text-gray-400 bg-white' : val === 'trial' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-emerald-300 bg-emerald-50 text-emerald-700'}`}
                      >
                        <option value="off">Tắt</option>
                        <option value="trial">Dùng thử {s.key === 'lab' ? 30 : 3}</option>
                        <option value="pro">PRO 365</option>
                      </select>
                      <div className="mt-0.5 h-3">{badge(sp)}</div>
                    </td>
                  )
                })}
                <td className="px-4 py-2.5 text-center whitespace-nowrap">
                  {(u.monthly_allowance || 0) > 0 && (
                    <div className="text-[10px] font-bold text-amber-600">Hạn mức {u.allowance_remaining ?? 0}/{u.monthly_allowance}</div>
                  )}
                  <span className="font-bold text-indigo-700" title="Ví mua thêm"><Coins className="w-3.5 h-3.5 inline mr-1 text-indigo-400" />{u.points}</span>
                  <button disabled={busy === `${u.id}:pts`} onClick={() => adjust(u.id)}
                    className="ml-2 text-[11px] font-bold text-indigo-600 hover:underline disabled:opacity-50">+/−</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-3">Ebook + PMaker là một gói chung, không còn PMaker PRO độc lập. PRO 365 ngày kèm <b>100 điểm/tháng</b>; <b>ví mua thêm</b> cộng dồn. Trừ hạn mức trước → ví sau. Dùng thử không trừ điểm: 3 ngày cho Ebook+PMaker/ENG, riêng Lab 30 ngày. ∞ = PRO cấp trước đây không hạn.</p>
    </div>
  )
}

export default MembershipManager
